import { useState, useEffect, useCallback, useRef } from "react";
import { Plus, Pencil, Trash2, Loader2, CalendarDays, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { getStaff, createStaff, updateStaff, deleteStaff, getBranches, getShopConfig } from "@/lib/api";
import ErrorBoundary from "@/components/ErrorBoundary";

const DAYS_OF_WEEK = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };

export default function StaffManagement() {
  const [staff, setStaff] = useState([]);
  const [branches, setBranches] = useState([]);
  const [superEnabled, setSuperEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    regular_roster: [],
    calculate_super: true,
    employment_status: "active",
    last_working_date: "",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [staffData, branchData, cfg] = await Promise.all([getStaff(), getBranches(), getShopConfig()]);
      setStaff(staffData);
      setBranches(branchData);
      setSuperEnabled(!!cfg.superannuation_enabled);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      name: "",
      contact: "",
      regular_roster: [],
      calculate_super: true,
      employment_status: "active",
      last_working_date: "",
    });
    setEditingStaff(null);
  };

  const handleOpenDialog = (staffMember = null) => {
    if (staffMember) {
      setEditingStaff(staffMember);
      setFormData({
        name: staffMember.name,
        contact: staffMember.contact || "",
        regular_roster: Array.isArray(staffMember.regular_roster) ? staffMember.regular_roster : [],
        calculate_super: staffMember.calculate_super !== false,
        employment_status: staffMember.employment_status || "active",
        last_working_date: staffMember.last_working_date || "",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      toast.error("Please enter a name");
      return;
    }

    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const data = {
        name: formData.name,
        contact: formData.contact,
        regular_roster: formData.regular_roster,
        calculate_super: superEnabled ? formData.calculate_super : false,
        employment_status: formData.employment_status,
        last_working_date: formData.employment_status === "inactive" ? (formData.last_working_date || null) : null,
      };

      if (editingStaff) {
        const updated = await updateStaff(editingStaff.id, data);
        setStaff(prev => prev.map(s => s.id === editingStaff.id ? updated : s));
        toast.success("Staff member updated");
      } else {
        const created = await createStaff(data);
        setStaff(prev => [...prev, created]);
        toast.success("Staff member created");
      }
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save staff member");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteStaff(deleteId);
      setStaff(prev => prev.filter(s => s.id !== deleteId));
      toast.success("Staff member deleted");
    } catch (error) {
      toast.error("Failed to delete staff member");
    } finally {
      setDeleteId(null);
    }
  };

  const toggleRosterDay = (day) => {
    setFormData(prev => {
      const roster = [...(prev.regular_roster ?? [])];
      const existingIdx = roster.findIndex(r => r.day === day);
      if (existingIdx >= 0) {
        roster.splice(existingIdx, 1);
      } else {
        roster.push({ day, branch_id: (branches ?? [])[0]?.id || "" });
      }
      return { ...prev, regular_roster: roster };
    });
  };

  const updateRosterBranch = (day, branchId) => {
    setFormData(prev => {
      const roster = (prev.regular_roster ?? []).map(r =>
        r.day === day ? { ...r, branch_id: branchId } : r
      );
      return { ...prev, regular_roster: roster };
    });
  };

  const getRosterSummary = (staffMember) => {
    const roster = staffMember.regular_roster || [];
    if (roster.length === 0) return "-";
    return roster.map(r => {
      const branch = branches.find(b => b.id === r.branch_id);
      const branchShort = branch ? (branch.id === "south-melbourne" ? "SM" : "DL") : "?";
      return `${DAY_LABELS[r.day]}@${branchShort}`;
    }).join(", ");
  };

  return (
    <div className="space-y-6 animate-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-forest">Staff Management</h2>
          <p className="text-sage">Onboard therapists, set employment status and weekly roster</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-forest hover:bg-forest-light"
          data-testid="add-staff-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Staff
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-sage/10 border border-sage/20 rounded-xl p-4">
        <p className="text-sm text-forest">
          <strong>Note:</strong> Set each therapist&apos;s <strong>Employment Status</strong> and weekly
          <strong> Regular Roster</strong>. Income guarantee and superannuation are configured globally in
          <strong> Admin → Settings → Financials</strong>.
        </p>
      </div>

      {/* Staff Table */}
      <div className="bg-white border border-border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-forest" />
          </div>
        ) : staff.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-sage">
            <p>No staff members found</p>
            <p className="text-sm">Add your first therapist to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="staff-table">
              <TableHeader>
                <TableRow className="bg-sand">
                  <TableHead className="table-header-cell">Name</TableHead>
                  <TableHead className="table-header-cell">Contact</TableHead>
                  <TableHead className="table-header-cell">Status</TableHead>
                  {superEnabled && <TableHead className="table-header-cell">Superannuation</TableHead>}
                  <TableHead className="table-header-cell">Regular Roster</TableHead>
                  <TableHead className="table-header-cell w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.map((s) => (
                  <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-sage">{s.contact || "-"}</TableCell>
                    <TableCell data-testid={`staff-status-${s.id}`}>
                      {s.employment_status === "inactive" ? (
                        <span className="inline-flex items-center gap-1 text-terracotta text-xs font-medium">
                          <X className="h-3.5 w-3.5" /> Inactive{s.last_working_date ? ` · to ${s.last_working_date}` : ""}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                          <Check className="h-3.5 w-3.5" /> Active
                        </span>
                      )}
                    </TableCell>
                    {superEnabled && (
                      <TableCell>
                        {s.calculate_super !== false ? (
                          <span className="inline-flex items-center gap-1 text-forest text-xs font-medium" data-testid={`staff-super-${s.id}`}>
                            <Check className="h-4 w-4 text-green-600" /> Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-sage text-xs font-medium" data-testid={`staff-super-${s.id}`}>
                            <X className="h-4 w-4 text-terracotta" /> No
                          </span>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <span className="text-xs text-sage" data-testid={`staff-roster-${s.id}`}>
                        {getRosterSummary(s)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenDialog(s)}
                          data-testid={`edit-staff-${s.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(s.id)}
                          data-testid={`delete-staff-${s.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">
              {editingStaff ? "Edit Staff Member" : "Add New Staff Member"}
            </DialogTitle>
            <DialogDescription>
              {editingStaff 
                ? "Update the staff member's information and weekly schedule"
                : "Add a new therapist with their weekly schedule"
              }
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-forest">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter name"
                data-testid="staff-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-forest">Contact</Label>
              <Input
                value={formData.contact}
                onChange={(e) => setFormData(prev => ({ ...prev, contact: e.target.value }))}
                placeholder="Phone number"
                data-testid="staff-contact-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-forest">Employment Status</Label>
                <Select
                  value={formData.employment_status}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, employment_status: val }))}
                >
                  <SelectTrigger data-testid="staff-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.employment_status === "inactive" && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-forest">Last Working Date</Label>
                  <Input
                    type="date"
                    value={formData.last_working_date || ""}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_working_date: e.target.value }))}
                    data-testid="staff-last-working-date"
                  />
                </div>
              )}
            </div>
            {formData.employment_status === "inactive" && (
              <p className="text-xs text-sage">Inactive therapists drop from the roster and schedule the day after their last working date.</p>
            )}

            {superEnabled && (
            <div className="flex items-center justify-between pt-2 border-t border-border" data-testid="staff-super-row">
              <div>
                <Label className="text-sm font-medium text-forest">Calculate Superannuation</Label>
                <p className="text-xs text-sage">If off, this therapist is excluded from super in the payroll ledger.</p>
              </div>
              <Switch
                checked={formData.calculate_super}
                onCheckedChange={(v) => setFormData((prev) => ({ ...prev, calculate_super: v }))}
                data-testid="staff-calculate-super-toggle"
              />
            </div>
            )}

            {/* Regular Roster Section */}
            <div className="space-y-3 pt-2 border-t border-border">
              <Label className="text-sm font-medium text-forest flex items-center gap-2">
                <CalendarDays className="h-4 w-4" />
                Regular Roster
              </Label>
              <p className="text-xs text-sage">Toggle working days and assign a branch for each day.</p>
              <ErrorBoundary
                fallback={
                  <p className="text-xs text-destructive" data-testid="roster-error-fallback">
                    Could not load the roster editor. Please close and reopen this dialog.
                  </p>
                }
              >
              <div className="space-y-2">
                {DAYS_OF_WEEK.map((day) => {
                  const entry = (formData.regular_roster ?? []).find(r => r.day === day);
                  const isActive = !!entry;
                  return (
                    <div key={day} className="flex items-center gap-3" data-testid={`roster-day-${day}`}>
                      <Switch
                        checked={isActive}
                        onCheckedChange={() => toggleRosterDay(day)}
                        data-testid={`roster-toggle-${day}`}
                      />
                      <span className={`text-sm w-12 font-medium ${isActive ? "text-forest" : "text-sage/50"}`}>
                        {DAY_LABELS[day]}
                      </span>
                      {isActive && (
                        <Select
                          value={entry?.branch_id || ""}
                          onValueChange={(val) => updateRosterBranch(day, val)}
                        >
                          <SelectTrigger className="h-8 text-xs flex-1" data-testid={`roster-branch-${day}`}>
                            <SelectValue placeholder="Select branch" />
                          </SelectTrigger>
                          <SelectContent>
                            {(branches ?? []).map((b) => (
                              <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  );
                })}
              </div>
              </ErrorBoundary>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
                data-testid="cancel-staff-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-forest hover:bg-forest-light"
                disabled={submitting}
                data-testid="save-staff-btn"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingStaff ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Staff Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this staff member? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-staff-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-staff-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

